import { cn } from '@/lib/utils';
import type { BooxCiSetId } from '@/lib/visual-preferences';

interface BooxCiMarkProps {
  ciSet: BooxCiSetId;
  className?: string;
  title?: string;
}

const INK = '#142033';

export function BooxCiMark({ ciSet, className, title }: BooxCiMarkProps) {
  return (
    <svg
      viewBox="0 0 96 96"
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      className={cn('block shrink-0', className)}
    >
      {title && <title>{title}</title>}
      {renderMark(ciSet)}
    </svg>
  );
}

function renderMark(ciSet: BooxCiSetId) {
  switch (ciSet) {
    case 'plush_cloud':
      return <PlushCloud />;
    case 'toy_capsule':
      return <ToyCapsule />;
    case 'pixel_dispatch':
      return <PixelDispatch />;
    case 'origami_route':
      return <OrigamiRoute />;
    case 'night_ops':
      return <NightOps />;
    case 'sticker_star':
      return <StickerStar />;
    case 'yard_utility':
      return <YardUtility />;
    case 'flat_ops':
    default:
      return <FlatOps />;
  }
}

function Antenna({ x = 48, y = 14, color = '#ff6f61' }: { x?: number; y?: number; color?: string }) {
  return (
    <>
      <path d={`M${x} ${y + 18}V${y + 5}`} stroke={INK} strokeWidth="4" strokeLinecap="round" />
      <circle cx={x} cy={y} r="7" fill={color} stroke={INK} strokeWidth="4" />
    </>
  );
}

function Face({ y = 42, smile = true, light = false }: { y?: number; smile?: boolean; light?: boolean }) {
  const stroke = light ? '#d9f7ff' : INK;
  const fill = light ? '#d9f7ff' : INK;
  return (
    <>
      <circle cx="37" cy={y} r="3.5" fill={fill} />
      <circle cx="59" cy={y} r="3.5" fill={fill} />
      <path
        d={smile ? `M42 ${y + 12}c4 4 8 4 12 0` : `M42 ${y + 12}h12`}
        fill="none"
        stroke={stroke}
        strokeWidth="3.5"
        strokeLinecap="round"
      />
    </>
  );
}

function FlatOps() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="28" ry="6" fill="#d7e1ed" />
      <Antenna />
      <rect x="19" y="26" width="58" height="54" rx="18" fill="#21b3a7" stroke={INK} strokeWidth="4" />
      <path d="M27 44h42M27 58h42" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />
      <Face y={42} />
      <rect x="65" y="31" width="18" height="24" rx="4" fill="#5b8def" stroke={INK} strokeWidth="3.5" transform="rotate(15 74 43)" />
    </>
  );
}

function PlushCloud() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="29" ry="6" fill="#d7e1ed" />
      <Antenna color="#ff9bb4" />
      <path d="M21 50c0-23 15-39 27-39s27 16 27 39v3c0 21-12 31-27 31S21 74 21 53z" fill="#ffe1e4" stroke={INK} strokeWidth="4" />
      <path d="M30 29c7-8 23-12 36 3" stroke="#fff" strokeWidth="9" strokeLinecap="round" opacity=".65" />
      <Face y={43} />
      <circle cx="29" cy="52" r="5" fill="#ff9bb4" />
      <circle cx="67" cy="52" r="5" fill="#ff9bb4" />
    </>
  );
}

function ToyCapsule() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="28" ry="6" fill="#d7e1ed" />
      <Antenna />
      <rect x="20" y="22" width="56" height="58" rx="19" fill="#42b4ff" stroke={INK} strokeWidth="4" />
      <path d="M28 34c12-10 32-12 43 0" stroke="#bdf3ff" strokeWidth="10" strokeLinecap="round" opacity=".7" />
      <rect x="28" y="38" width="40" height="25" rx="10" fill="#eaf9ff" stroke={INK} strokeWidth="3.5" />
      <Face y={49} />
      <path d="M32 70h32" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />
    </>
  );
}

function PixelDispatch() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="29" ry="6" fill="#d7e1ed" />
      <g shapeRendering="crispEdges">
        <rect x="31" y="14" width="34" height="10" fill={INK} />
        <rect x="44" y="7" width="8" height="8" fill="#ff6f61" />
        <rect x="17" y="24" width="62" height="48" fill={INK} />
        <rect x="25" y="32" width="46" height="32" fill="#8ff2df" />
        <rect x="34" y="41" width="7" height="7" fill={INK} />
        <rect x="55" y="41" width="7" height="7" fill={INK} />
        <rect x="41" y="55" width="14" height="5" fill={INK} />
        <rect x="71" y="40" width="10" height="22" fill="#5b8def" />
      </g>
    </>
  );
}

function OrigamiRoute() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="29" ry="6" fill="#d7e1ed" />
      <Antenna color="#ffc857" />
      <path d="M17 45 48 18l31 27-31 30z" fill="#fff" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      <path d="M17 45l31-4 31 4M48 18v57M31 61l17-20 17 20" stroke={INK} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <Face y={46} />
      <path d="M19 32c-9-6-17-4-22 4M77 32c9-6 17-4 22 4" stroke="#ffc857" strokeWidth="5" strokeLinecap="round" />
    </>
  );
}

function NightOps() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="28" ry="6" fill="#d7e1ed" />
      <Antenna />
      <rect x="20" y="19" width="56" height="62" rx="17" fill="#111827" stroke={INK} strokeWidth="4" />
      <rect x="29" y="33" width="38" height="32" rx="12" fill="#d9f7ff" />
      <Face y={45} />
      <path d="M68 23l11-11M75 30l15-4" stroke="#ffc857" strokeWidth="4" strokeLinecap="round" />
    </>
  );
}

function StickerStar() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="29" ry="6" fill="#d7e1ed" />
      <Antenna />
      <path d="m48 15 9 17 20-4-8 18 14 14-20 3-4 20-11-15-13 15-3-20-20-3 14-14-8-18 20 4z" fill="#ffc857" stroke={INK} strokeWidth="4" strokeLinejoin="round" />
      <Face y={48} />
      <circle cx="31" cy="58" r="4" fill="#ff9f9a" />
      <circle cx="65" cy="58" r="4" fill="#ff9f9a" />
    </>
  );
}

function YardUtility() {
  return (
    <>
      <ellipse cx="48" cy="84" rx="29" ry="6" fill="#d7e1ed" />
      <Antenna />
      <rect x="21" y="22" width="54" height="60" rx="17" fill="#f4f0e6" stroke={INK} strokeWidth="4" />
      <Face y={40} />
      <path d="M30 55h36M30 68h25" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M22 34 9 23M74 34l13-11" stroke={INK} strokeWidth="3.5" strokeLinecap="round" />
      <path d="m9 23-7-6M87 23l7-6" stroke="#ff9a42" strokeWidth="5" strokeLinecap="round" />
    </>
  );
}
